import { type ArrowPaths } from '~/components/features/SegmentRouteArrow'
import { type CustomerStrategySegmentRoute } from '~/gql/graphql'

// レイアウト定数
export const cellHeight = 134
export const cellWidth = 120
export const gap = 2
const cellPad = 12
const lineHeight = 25

// 矢印描画定数
const arrowWidth = 4
const arrowBiteDepth = 20
const arrowHeadV = 16
const arrowHeadH = 16
const arrowDistanceFromTextCenter = 9
const borderWidth = 2
const multilineTextMargin = 14

const arrowHeadHalfH = arrowHeadH / 2
const hypotenuse = (arrowHeadHalfH ** 2 + arrowHeadV ** 2) ** (1 / 2)
const borderDiffH =
  (borderWidth * arrowHeadHalfH * (1 + hypotenuse / arrowHeadHalfH)) /
  arrowHeadV
const borderDiffV = (borderWidth * hypotenuse) / arrowHeadHalfH

const cellCenterX = cellWidth / 2
const topTextCenterY = 1 + cellPad + lineHeight / 2
const bottomTextCenterY = cellHeight - topTextCenterY
const topUpperY = topTextCenterY - arrowDistanceFromTextCenter - arrowWidth
const topLowerY =
  topTextCenterY + arrowDistanceFromTextCenter + multilineTextMargin
const bottomUpperY =
  bottomTextCenterY - arrowDistanceFromTextCenter - arrowWidth
const bottomLowerY = bottomTextCenterY + arrowDistanceFromTextCenter
const negativeTopUpperUnderY = cellHeight + gap + topUpperY + arrowWidth
const centerY = (cellHeight - arrowWidth + multilineTextMargin) / 2

const arrowOverhang = (arrowHeadH - arrowWidth) / 2
const radius = arrowHeadH / 2
const shaft = arrowBiteDepth + gap + arrowBiteDepth - arrowHeadV

// パス計算ユーティリティ
type Point = [number, number]

const toAbsolutePoints = (relativePoints: Point[]): Point[] =>
  relativePoints.reduce<Point[]>((prev, [moveX, moveY]) => {
    const [x, y] = prev[prev.length - 1] ?? [0, 0]

    return [...prev, [x + moveX, y + moveY]]
  }, [])

const sumPoints = (points1: Point[], points2: Point[]): Point[] =>
  points1.map(([x1, y1], index) => {
    const [x2, y2] = points2[index]

    return [x1 + x2, y1 + y2]
  })

const pointsToDraw = (points: Point[]): string =>
  `${points
    .map((point, index) => [index === 0 ? 'M' : 'L', ...point].join(' '))
    .join(' ')} Z`

export type Position = 'center' | 'lower' | 'upper'

export const computeArrowPaths = (
  position: Position,
  segmentRoute: CustomerStrategySegmentRoute,
): ArrowPaths => {
  const calcIntermediateData = (): [Point[], Point[]] => {
    const from5segs = Math.floor((segmentRoute.fromSegmentNumber + 1) / 2)
    const to5segs = Math.floor((segmentRoute.toSegmentNumber + 1) / 2)

    const startOffsetX = (cellWidth + gap) * (5 - from5segs)

    if (from5segs === to5segs) {
      return [
        [
          [startOffsetX + cellCenterX - arrowWidth / 2, negativeTopUpperUnderY],
          [0, -(arrowWidth + topUpperY + gap + 1)],
          [-arrowOverhang, 0],
          [radius, -arrowHeadV],
          [radius, arrowHeadV],
          [-arrowOverhang, 0],
          [0, 1 + gap + topUpperY + arrowWidth],
        ],
        [
          [-borderWidth, borderWidth],
          [-borderWidth, borderWidth],
          [-borderDiffH, borderWidth],
          [0, -borderDiffV],
          [borderDiffH, borderWidth],
          [borderWidth, borderWidth],
          [borderWidth, borderWidth],
        ],
      ]
    }

    const startX = startOffsetX + cellWidth - arrowBiteDepth
    const shaftLength = shaft + (cellWidth + gap) * (from5segs - to5segs - 1)

    if (position === 'center' || segmentRoute.fromSegmentNumber % 2 === 1) {
      return [
        [
          [
            startX,
            position === 'center'
              ? centerY
              : position === 'upper'
                ? topUpperY
                : topLowerY,
          ],
          [shaftLength, 0],
          [0, -arrowOverhang],
          [arrowHeadV, radius],
          [-arrowHeadV, radius],
          [0, -arrowOverhang],
          [-shaftLength, 0],
        ],
        [
          [-borderWidth, -borderWidth],
          [-borderWidth, -borderWidth],
          [-borderWidth, -borderDiffH],
          [borderDiffV, 0],
          [-borderWidth, borderDiffH],
          [-borderWidth, borderWidth],
          [-borderWidth, borderWidth],
        ],
      ]
    }

    return [
      [
        [startX, position === 'upper' ? bottomUpperY : bottomLowerY],
        [shaftLength, 0],
        [0, -arrowOverhang],
        [arrowHeadV, radius],
        [-arrowHeadV, radius],
        [0, -arrowOverhang],
        [-shaftLength + arrowWidth, 0],
        [
          0,
          cellHeight -
            (position === 'upper' ? bottomUpperY : bottomLowerY) +
            gap +
            topUpperY +
            arrowWidth,
        ],
        [-arrowWidth, 0],
      ],
      [
        [-borderWidth, -borderWidth],
        [-borderWidth, -borderWidth],
        [-borderWidth, -borderDiffH],
        [borderDiffV, 0],
        [-borderWidth, borderDiffH],
        [-borderWidth, borderWidth],
        [borderWidth, borderWidth],
        [borderWidth, borderWidth],
        [-borderWidth, borderWidth],
      ],
    ]
  }

  const [arrowRelativePoints, borderDiffPoints] = calcIntermediateData()

  const arrowPoints = toAbsolutePoints(arrowRelativePoints)
  const borderPoints = sumPoints(arrowPoints, borderDiffPoints)

  return {
    arrowDraw: pointsToDraw(arrowPoints),
    borderDraw: pointsToDraw(borderPoints),
  }
}
